'use client';

import { faExternalLink, faUserCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { buildLoginUrl } from '@pickleballinc/lib/session/login-url';
import { Application } from '@pickleballinc/lib/types';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import CustomLink from '../ui/custom-link';

import NotificationsPopup from './Notification/NotificationsPopup';
import { useUser } from './UserInfoProvider';
import { UserProfile } from './UserProfile';
import { NotificationLookup } from './utils/types';

export interface TopNavigationProps {
	notifications: NotificationLookup | undefined;
	canAccessManage: boolean;
	application: Application;
	pickleballHref: string;
	tournamentHref: string;
	leaguesHref: string;
	clubsHref: string;
	teamLeaguesHref: string;
	cdnUrl: string;
	checkoutLink: string;
	ssoLink: string;
	onSSOClick?: () => void;
}

export function TopNavigation({
	notifications,
	canAccessManage,
	application,
	pickleballHref,
	tournamentHref,
	leaguesHref,
	clubsHref,
	teamLeaguesHref,
	cdnUrl,
	checkoutLink,
	ssoLink,
	onSSOClick
}: TopNavigationProps) {
	const user = useUser();
	const pathname = usePathname();
	const [baseURL, setBaseURL] = useState('');

	useEffect(() => {
		setBaseURL(window.location.href);
	}, []);

	const isTourneyPage = application === 'tournament';
	const isTeamLeaguesPage = application === 'team-leagues';
	const isAprPage = application === 'apr-rankings';
	const isClubsPage = application === 'clubs';
	const isLeaguesPage = application === 'leagues';
	const isPickleball = application === 'pickleball' || isLeaguesPage || isAprPage;
	// Whether this app runs on localhost (its own URL, like `MainHeader`): Log In and Logout then go
	// to its local `/login` / `/logout` instead of SSO.
	const ownHref = isPickleball
		? pickleballHref
		: isClubsPage
			? clubsHref
			: isTeamLeaguesPage
				? teamLeaguesHref
				: isTourneyPage
					? tournamentHref
					: '';
	const isLocalHost = ownHref.includes('localhost');
	// Log In: the local `/login` on localhost (with the path until hydration sets `baseURL`, so the
	// server HTML's link comes back here too), else SSO, which needs the full URL.
	const loginHref = buildLoginUrl({
		ssoUrl: ssoLink,
		isLocalhost: !!isLocalHost,
		continueTo: isLocalHost ? baseURL || pathname : baseURL
	});

	const topNavigationClasses = clsx('hidden text-white sm:block', {
		'!bg-[#EF612D]': isTeamLeaguesPage,
		'!bg-[#4CA30D]': isAprPage,
		'!bg-clubs-700': isClubsPage,
		'!bg-leagues-300': isLeaguesPage,
		'bg-experiment': !isTourneyPage && !isTeamLeaguesPage && !isAprPage && !isClubsPage && !isLeaguesPage,
		'!bg-blue-600': isTourneyPage
	});

	const pickleballCentralClasses = 'inline-flex items-center gap-2 pr-4 text-sm font-medium underline-offset-4 hover:underline decoration-white';

	const loginButtonClasses = clsx(
		'mr-2 flex items-center gap-2 !bg-transparent !py-1.5 text-sm font-medium !leading-none text-gray-700 hover:!bg-transparent',
		{
			'text-white hover:!text-white/80': !isTourneyPage,
			'text-white hover:!bg-transparent hover:!text-white/80': isTourneyPage,
			'cursor-pointer': onSSOClick
		}
	);

	const signUpClasses = clsx('rounded !py-1.5 px-2 text-sm font-medium !leading-none', {
		'!bg-white !text-[#EF6820] hover:!text-[#EF6820]': isTeamLeaguesPage,
		'!bg-white !text-[#4CA30D] hover:!text-[#4CA30D]': isAprPage,
		'!bg-white !text-error-700 hover:!text-error-700': isClubsPage,
		'!bg-white !text-leagues-300 hover:!text-leagues-300': isLeaguesPage,
		'!bg-white !text-experiment hover:!text-experiment': !isTourneyPage && !isTeamLeaguesPage && !isAprPage && !isClubsPage && !isLeaguesPage,
		'!bg-white !text-blue-600 hover:!text-blue-600': isTourneyPage
	});

	return (
		<div className={topNavigationClasses} id="pb-topnav">
			<ul className="mx-auto flex h-10 max-w-[1280px] items-center justify-between px-4">
				<li className="divide-x divide-gray-400">
					<CustomLink
						href="https://pickleballcentral.com"
						target="_blank"
						className={pickleballCentralClasses}
						data-testid={`pre-header-link-pickleball-central`}
					>
						<span className="inline-flex">Shop Pickleball Central</span>
						<FontAwesomeIcon icon={faExternalLink} size="sm" />
					</CustomLink>
				</li>
				<li className="divide-x divide-gray-400">
					<CustomLink
						href={`http${isLocalHost && isPickleball ? '' : 's'}://${pickleballHref}/watch-now`}
						className={pickleballCentralClasses}
						data-testid={`pre-header-link-watch-now`}
					>
						<span className="inline-flex">Where to Watch Pickleball</span>
					</CustomLink>
				</li>
				<li className="flex items-center gap-2" data-testid={`pre-header-user-profile`}>
					{user?.uuid ? (
						<>
							<NotificationsPopup notifications={notifications} checkoutLink={`${checkoutLink}/pending`} />
							<UserProfile
								cdnUrl={cdnUrl}
								pickleballHref={pickleballHref}
								tournamentHref={tournamentHref}
								leaguesHref={leaguesHref}
								clubsHref={clubsHref}
								teamLeaguesHref={teamLeaguesHref}
								ssoLink={ssoLink}
								user={user}
								canAccessManage={canAccessManage}
								isLocalhost={isLocalHost}
								pathname={pathname}
								isPickleball={isPickleball}
							/>
						</>
					) : (
						<>
							{onSSOClick && !isLocalHost ? (
								<button
									className={loginButtonClasses}
									rel="nofollow"
									data-testid={`pre-header-user-profile-login`}
									onClick={onSSOClick}
								>
									<FontAwesomeIcon icon={faUserCircle} className="text-lg" />
									Log In
								</button>
							) : isLocalHost ? (
								<CustomLink
									href={loginHref}
									className={loginButtonClasses}
									rel="nofollow"
									data-testid={`pre-header-user-profile-login`}
								>
									<FontAwesomeIcon icon={faUserCircle} className="text-lg" />
									Log In
								</CustomLink>
							) : (
								<a href={loginHref} className={loginButtonClasses} rel="nofollow" data-testid={`pre-header-user-profile-login`}>
									<FontAwesomeIcon icon={faUserCircle} className="text-lg" />
									Log In
								</a>
							)}
							<a
								href={`${ssoLink}/v3/signup?continue=${encodeURIComponent(baseURL)}`}
								className={signUpClasses}
								rel="nofollow"
								data-testid={`pre-header-user-profile-register`}
							>
								Sign Up
							</a>
						</>
					)}
				</li>
			</ul>
		</div>
	);
}
